import React, { useState } from 'react';
import { useTheme } from '../../../Context/ThemeContext';
import { NotificationHandler, FormState } from './NotificationHandler';

const defaultForm: FormState = {
    title: '',
    message: '',
    sendToAll: false,
    userIdsInput: '',
};

const NotificationPage: React.FC = () => {
    const { theme, isMobile } = useTheme();

    const [form, setForm] = useState<FormState>(defaultForm);
    const [sending, setSending] = useState(false);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleChange = (field: keyof FormState, value: string | boolean) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setFeedback(null);
    };

    const isFormValid = (): boolean => {
        if (!form.title.trim()) return false;
        if (!form.message.trim()) return false;
        if (!form.sendToAll && !form.userIdsInput.trim()) return false;
        return true;
    };

    const handleSubmit = () => {
        if (!isFormValid()) return;
        setShowConfirm(true);
    };

    const handleConfirmSend = async () => {
        setShowConfirm(false);
        setSending(true);
        setFeedback(null);

        try {
            const handler = new NotificationHandler();

            const recipientUserIds = form.sendToAll
                ? []
                : form.userIdsInput
                    .split(',')
                    .map((id) => id.trim())
                    .filter((id) => id.length > 0);

            await handler.sendNotification(
                recipientUserIds,
                form.sendToAll,
                form.title.trim(),
                form.message.trim()
            );

            setFeedback({ type: 'success', message: 'Notification sent successfully!' });
            setForm(defaultForm);
        } catch (err) {
            console.error('Error sending notification:', err);
            setFeedback({ type: 'error', message: 'Failed to send notification. Please try again.' });
        } finally {
            setSending(false);
        }
    };

    const styles: Record<string, React.CSSProperties> = {
        container: {
            padding: 0,
            maxWidth: '700px',
            margin: '0 auto',
        },
        title: {
            fontSize: isMobile ? '22px' : '28px',
            fontWeight: 700,
            color: theme.titleColor,
            marginBottom: '8px',
            textAlign: 'center' as const,
        },
        subtitle: {
            fontSize: '14px',
            color: theme.textSecondary,
            marginBottom: '24px',
            textAlign: 'center' as const,
        },
        formCard: {
            background: theme.card,
            borderRadius: '10px',
            padding: isMobile ? '16px' : '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
        fieldGroup: {
            marginBottom: '20px',
        },
        label: {
            display: 'block',
            fontSize: '14px',
            fontWeight: 600,
            color: theme.text,
            marginBottom: '6px',
        },
        input: {
            width: '100%',
            padding: '10px 14px',
            fontSize: '14px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            background: theme.inputBg,
            color: theme.text,
            outline: 'none',
            boxSizing: 'border-box' as const,
        },
        textarea: {
            width: '100%',
            padding: '10px 14px',
            fontSize: '14px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            background: theme.inputBg,
            color: theme.text,
            outline: 'none',
            boxSizing: 'border-box' as const,
            minHeight: '120px',
            resize: 'vertical' as const,
            fontFamily: 'inherit',
        },
        checkboxRow: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '20px',
        },
        checkbox: {
            width: '18px',
            height: '18px',
            cursor: 'pointer',
            accentColor: '#3b82f6',
        },
        checkboxLabel: {
            fontSize: '14px',
            fontWeight: 500,
            color: theme.text,
            cursor: 'pointer',
        },
        helperText: {
            fontSize: '12px',
            color: theme.textSecondary,
            marginTop: '4px',
        },
        submitButton: {
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#fff',
            background: '#3b82f6',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            opacity: isFormValid() && !sending ? 1 : 0.5,
            transition: 'opacity 0.2s',
        },
        feedbackSuccess: {
            padding: '12px 16px',
            borderRadius: '8px',
            background: 'rgba(34, 197, 94, 0.15)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            color: '#22c55e',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '16px',
        },
        feedbackError: {
            padding: '12px 16px',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#ef4444',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '16px',
        },
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        confirmModal: {
            background: theme.card,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '420px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
        confirmTitle: {
            fontSize: '18px',
            fontWeight: 700,
            color: theme.text,
            marginBottom: '12px',
        },
        confirmText: {
            fontSize: '14px',
            color: theme.textSecondary,
            marginBottom: '20px',
            lineHeight: 1.5,
        },
        confirmActions: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
        },
        confirmCancel: {
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: 500,
            color: theme.text,
            background: 'transparent',
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            cursor: 'pointer',
        },
        confirmSend: {
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#fff',
            background: '#3b82f6',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
        },
        spinner: {
            display: 'inline-block',
            width: '16px',
            height: '16px',
            border: '2px solid rgba(255,255,255,0.3)',
            borderTop: '2px solid #fff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            verticalAlign: 'middle',
            marginRight: '8px',
        },
    };

    return (
        <div style={styles.container}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

            <h1 style={styles.title}>Send Notification</h1>
            <p style={styles.subtitle}>Send push notifications to players</p>

            {feedback && (
                <div style={feedback.type === 'success' ? styles.feedbackSuccess : styles.feedbackError}>
                    {feedback.message}
                </div>
            )}

            <div style={styles.formCard}>
                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Title</label>
                    <input
                        type="text"
                        placeholder="Notification title..."
                        value={form.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        style={styles.input}
                    />
                </div>

                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Message</label>
                    <textarea
                        placeholder="Notification message..."
                        value={form.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        style={styles.textarea}
                    />
                </div>

                <div style={styles.checkboxRow}>
                    <input
                        type="checkbox"
                        id="sendToAll"
                        checked={form.sendToAll}
                        onChange={(e) => handleChange('sendToAll', e.target.checked)}
                        style={styles.checkbox}
                    />
                    <label htmlFor="sendToAll" style={styles.checkboxLabel}>
                        Send to All Players
                    </label>
                </div>

                {!form.sendToAll && (
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>Recipient User IDs</label>
                        <input
                            type="text"
                            placeholder="Enter comma-separated user IDs..."
                            value={form.userIdsInput}
                            onChange={(e) => handleChange('userIdsInput', e.target.value)}
                            style={styles.input}
                        />
                        <div style={styles.helperText}>
                            Enter user IDs separated by commas (e.g., id1, id2, id3)
                        </div>
                    </div>
                )}

                <button
                    style={styles.submitButton}
                    onClick={handleSubmit}
                    disabled={!isFormValid() || sending}
                >
                    {sending && <span style={styles.spinner} />}
                    {sending ? 'Sending...' : 'Send Notification'}
                </button>
            </div>

            {/* Confirmation Modal */}
            {showConfirm && (
                <div style={styles.overlay} onClick={() => setShowConfirm(false)}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.confirmTitle}>Confirm Notification</div>
                        <div style={styles.confirmText}>
                            {form.sendToAll
                                ? 'You are about to send a notification to ALL players. This action cannot be undone.'
                                : `You are about to send a notification to ${form.userIdsInput.split(',').filter(id => id.trim()).length} recipient(s).`}
                            <br /><br />
                            <strong>Title:</strong> {form.title}<br />
                            <strong>Message:</strong> {form.message.length > 100 ? form.message.substring(0, 100) + '...' : form.message}
                        </div>
                        <div style={styles.confirmActions}>
                            <button style={styles.confirmCancel} onClick={() => setShowConfirm(false)}>
                                Cancel
                            </button>
                            <button style={styles.confirmSend} onClick={handleConfirmSend}>
                                Confirm Send
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationPage;
